import React, { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CodeHighlighter } from '@uhg-abyss/web/ui/CodeHighlighter';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Tabs } from '@uhg-abyss/web/ui/Tabs';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { LagoonContext } from '@src/AppContext';

const BodyContainer = styled('div', {
  marginTop: 24,
});

export const BodyV2 = ({ body, currentApi }) => {
  const service = 'superconnector';
  const formattedCode = JSON.stringify(body, null, 2);
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('configs');
  const serviceToggle = featureToggles?.getRow('SERVICE_TOGGLE')?.value;
  const [bodySchema, setBodySchema] = useState({});
  const serviceDescriptionToggle = featureToggles?.getRow(
    'SERVICE_DESCRIPTION_TOGGLE'
  )?.value;
  const { schema } = currentApi;

  const getUpdatedProperty = (defData, key, required, updatedSchema) => {
    if (defData && Object.keys(defData).length > 0) {
      if (defData.properties) {
        return {
          ...updatedSchema,
          [key]: {
            type: 'object',
            properties: defData.properties,
            description: defData.description,
            required,
          },
        };
      }
      return {
        ...updatedSchema,
        [key]: {
          type: defData.type,
          description: defData.description,
          required,
        },
      };
    }
    return updatedSchema;
  };

  const getUpdatedArrayProperty = (
    data,
    defProperties,
    key,
    required,
    updatedSchema,
    visited
  ) => {
    // Default type if we can't determine it
    let arrayItemType = 'object';

    if (defProperties[key]?.items?.$ref) {
      const defData = getDefinition(
        data,
        defProperties[key].items.$ref,
        visited
      );

      if (defData && Object.keys(defData).length > 0) {
        return {
          ...updatedSchema,
          [key]: {
            type: 'array',
            items: {
              properties: defData.properties || {},
            },
            description: defData.description,
            required,
          },
        };
      }

      const refType =
        defProperties[key].items.$ref.split('/').pop() || 'object';
      return {
        ...updatedSchema,
        [key]: {
          type: `array[${refType}]`,
          required,
        },
      };
    } else if (defProperties[key]?.items?.properties) {
      return {
        ...updatedSchema,
        [key]: {
          type: 'array',
          items: {
            properties: defProperties[key].items.properties,
          },
          description: defProperties[key]?.description,
          required,
        },
      };
    } else if (defProperties[key]?.items?.type) {
      arrayItemType = defProperties[key].items.type;
      return {
        ...updatedSchema,
        [key]: {
          type: `array[${arrayItemType}]`,
          description: defProperties[key]?.description,
          required,
        },
      };
    }

    return {
      ...updatedSchema,
      [key]: {
        type: `array[${arrayItemType}]`,
        description: defProperties[key]?.description,
        required,
      },
    };
  };

  const getDefinition = (data, ref, visited = new Map()) => {
    const definition = ref.split('/').pop();

    // Check for cyclic definitions
    if (visited.has(definition)) {
      return visited.get(definition); // Return the visited definition
    }

    // Placeholder for cyclic references
    visited.set(definition, {});

    const definitionBlock = data[definition];
    const defProperties = definitionBlock?.properties;
    let updatedSchema = {};
    if (!defProperties) {
      visited.set(definition, definitionBlock);
      return definitionBlock;
    }
    Object.keys(defProperties).forEach((key) => {
      let required = false;
      if (definitionBlock.required?.includes(key)) {
        required = true;
      }
      if (defProperties[key]?.$ref) {
        const defData = getDefinition(data, defProperties[key].$ref, visited);
        updatedSchema = getUpdatedProperty(
          defData,
          key,
          required,
          updatedSchema
        );
      } else if (defProperties[key]?.type === 'array') {
        // eslint-disable-next-line no-use-before-define
        updatedSchema = getUpdatedArrayProperty(
          data,
          defProperties,
          key,
          required,
          updatedSchema,
          visited
        );
      } else if (defProperties[key]?.enum) {
        updatedSchema = {
          ...updatedSchema,
          [key]: {
            type: 'string',
            required,
            description: defProperties[key]?.description,
          },
        };
      } else if (defProperties[key]?.allOf?.length > 0) {
        const updatedDefData = {
          properties: {},
        };
        defProperties[key].allOf.forEach((item) => {
          if (item.$ref) {
            const defData = getDefinition(data, item.$ref, visited);
            if (defData?.properties) {
              Object.assign(updatedDefData.properties, defData.properties);
            }
          }
        });
        updatedSchema = getUpdatedProperty(
          updatedDefData,
          key,
          required,
          updatedSchema
        );
      } else {
        updatedSchema = {
          ...updatedSchema,
          [key]: {
            ...defProperties[key],
            required,
          },
        };
      }
    });
    let updatedDefinitions = {};
    updatedDefinitions = {
      ...data[definition],
      properties: updatedSchema,
    };

    // Update the visited definition with the actual definition
    visited.set(definition, updatedDefinitions);

    return updatedDefinitions;
  };

  const asteriskText = (key) => {
    return (
      <span>
        {key}
        <span style={{ color: '#C40000' }}> *</span>
      </span>
    );
  };

  const col = [
    {
      Header: 'Field',
      accessor: 'field',
    },
    {
      Header: 'Type',
      accessor: 'type',
      maxWidth: 50,
    },
  ];
  if (serviceDescriptionToggle?.includes(service)) {
    col.push({ Header: 'Description', accessor: 'description' });
  }

  const columns = React.useMemo(() => {
    return col;
  }, [service]);
  const formattedSchema = (data) => {
    if (!data) return [];
    const schemaData = Object.keys(data).map((key) => {
      const value = data[key];
      let { type } = value;
      if (!type && value.$ref) {
        const referencedSchema = getDefinition(
          schema || currentApi?.schema || {},
          value.$ref,
          new Map()
        );

        type = referencedSchema?.type || 'object';

        if (referencedSchema?.properties) {
          type = 'object';
          const subRows = formattedSchema(referencedSchema.properties);
          return {
            field: data[key].required ? asteriskText(key) : key,
            type,
            subRows,
            description:
              referencedSchema?.description?.substring(0, 350) ||
              value?.description?.substring(0, 350),
          };
        }
      }
      if (data[key].type === 'object') {
        type = 'object';
        const subRows = formattedSchema(value.properties);
        return {
          field: data[key].required ? asteriskText(key) : key,
          type,
          subRows,
          description: value?.description?.substring(0, 350),
        };
      }
      if (data[key].type === 'array') {
        type = 'array';
        let subRows = [];

        if (value?.items?.properties) {
          subRows = formattedSchema(value.items.properties);
        } else if (value?.items?.type === 'object') {
          subRows = formattedSchema(value.items.properties || {});
        }

        return {
          field: data[key].required ? asteriskText(key) : key,
          type: value?.items?.type ? `array[${value.items.type}]` : 'array',
          subRows,
          description: value?.description?.substring(0, 350),
        };
      }
      return {
        field: data[key].required ? asteriskText(key) : key,
        type,
        description: value?.description?.substring(0, 350),
      };
    });
    return schemaData;
  };
  const data = useMemo(() => {
    return [...formattedSchema(bodySchema?.properties)];
  }, [currentApi]);

  const dataTableProps = useDataTable({
    initialData: data,
    initialColumns: columns,
    showExpansion: true,
  });

  useEffect(() => {
    const requestBody = currentApi.requestBody;
    if (!requestBody || !requestBody.content) {
      setBodySchema({});
      return;
    }
    const contentTypes = Object.keys(requestBody.content);
    if (contentTypes.length === 0) {
      setBodySchema({});
      return;
    }
    const content = requestBody.content[contentTypes[0]];
    if (!content) {
      setBodySchema({});
      return;
    }
    if (!content.schema) {
      setBodySchema({});
      return;
    }
    if (content.schema.$ref) {
      const bodySchemaDef = getDefinition(
        content,
        schema && Object.keys(schema).length > 0
          ? schema
          : currentApi?.schema || {},
        content.schema.$ref
      );
      setBodySchema(bodySchemaDef);
      return;
    }
    setBodySchema(content.schema);
  }, [currentApi]);

  useEffect(() => {
    dataTableProps.setColumns(columns, true);
    dataTableProps.setData(formattedSchema(bodySchema.properties), true);
  }, [bodySchema]);

  return (
    <BodyContainer>
      {serviceToggle?.includes(service) ? (
        <Tabs title="Arrow Variant" variant="arrow">
          <Tabs.Tab label="Schema">
            <DataTable tableState={dataTableProps} />
          </Tabs.Tab>
          <Tabs.Tab label="Sample">
            <CodeHighlighter
              language="js"
              code={formattedCode}
              maxHeight="450px"
            />
          </Tabs.Tab>
        </Tabs>
      ) : (
        <CodeHighlighter language="js" code={formattedCode} maxHeight="450px" />
      )}
    </BodyContainer>
  );
};

BodyV2.propTypes = {
  body: PropTypes.shape({}),
  currentApi: PropTypes.shape({}).isRequired,
};

BodyV2.defaultProps = {
  body: null,
};
